/** The factor library, read — **gate 20 (S12)**.
 *
 * `GET /api/v1/factor-sets` returns every locked factor set the **database** holds (KD, 2026-10-23:
 * the rows a receipt cites, not the engine's in-memory copy), each with its factors. There is
 * nothing else in this file: no mutation, no edition argument, no custom factor. The library holds
 * one edition per jurisdiction, so there is nothing to choose between, and a factor set is locked
 * when it is loaded (ADR-0012). `FactorLibrary.test.tsx` asserts the module's export list.
 *
 * Response types come from the generated API schema. Runtime shape checks remain necessary:
 * TypeScript cannot validate JSON received over HTTP. A response with the wrong shape is a
 * failed read, never a library with fewer rows in it.
 *
 * **Rule 5.** Every `value` and every component is a decimal *string*, and stays one. Nothing in
 * this module converts a factor to `number`; trimming the column's trailing zeros is the screen's
 * job (`lib/format.factorValue`) and is a string operation.
 *
 * **Not cached beyond the session.** Reference data changes only by an operator running the loader,
 * but a cached library is exactly the stale-fact failure this screen exists to avoid, and it is a
 * single small request.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type FactorLibraryFactor = components["schemas"]["FactorLibraryFactorOut"];
export type FactorLibrarySet = components["schemas"]["FactorLibrarySetOut"];
export type FactorLibrary = components["schemas"]["FactorLibraryOut"];

/** The exact key sets, exported so a test can pin them against the server's. */
export const FACTOR_SET_KEYS = [
  "attribution_statement",
  "edition",
  "factors",
  "gwp_set_label",
  "jurisdiction",
  "key",
  "label",
  "licence",
  "locked",
  "locked_at",
  "prices_new_figures",
  "published_at",
  "publisher",
  "retrieved_at",
  "source_sha256",
  "source_url",
] as const;

export const FACTOR_KEYS = [
  "category",
  "components",
  "edition",
  "external_id",
  "fuel_or_carrier",
  "notes",
  "publisher",
  "region",
  "source_published_at",
  "source_sha256",
  "source_url",
  "unit_in",
  "unit_out",
  "value",
  "verification_row",
] as const;

const NOT_THE_SHAPE =
  "The factor library came back in a shape this screen does not recognise, so nothing is shown rather than something wrong.";

const DECIMAL = /^-?\d+(\.\d+)?$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.every((key) => key in value);
}

function stringOrNull(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function isFactor(value: unknown): value is FactorLibraryFactor {
  if (!isRecord(value) || !hasKeys(value, FACTOR_KEYS)) return false;
  const strings = ["category", "fuel_or_carrier", "region", "unit_in", "unit_out", "publisher", "edition", "verification_row"];
  if (!strings.every((key) => typeof value[key] === "string")) return false;
  // A JSON number here means the server stopped sending strings (rule 5); refuse it rather than
  // render a float.
  if (typeof value.value !== "string" || !DECIMAL.test(value.value)) return false;
  if (!stringOrNull(value.external_id) || !stringOrNull(value.notes)) return false;
  // A row's own source is all three fields or none; a half is a citation that cannot be checked.
  const own = [value.source_url, value.source_published_at, value.source_sha256];
  if (!own.every(stringOrNull) || own.some((part) => part === null) !== own.every((part) => part === null)) {
    return false;
  }
  const components = value.components;
  if (components === null) return true;
  return (
    isRecord(components) &&
    Object.values(components).every((part) => typeof part === "string" && DECIMAL.test(part))
  );
}

function isSet(value: unknown): value is FactorLibrarySet {
  if (!isRecord(value) || !hasKeys(value, FACTOR_SET_KEYS)) return false;
  const strings = ["key", "label", "jurisdiction", "publisher", "edition", "retrieved_at", "gwp_set_label"];
  if (!strings.every((key) => typeof value[key] === "string")) return false;
  const nullable = ["published_at", "source_url", "source_sha256", "licence", "attribution_statement", "locked_at"];
  if (!nullable.every((key) => stringOrNull(value[key]))) return false;
  return (
    typeof value.locked === "boolean" &&
    typeof value.prices_new_figures === "boolean" &&
    Array.isArray(value.factors) &&
    value.factors.every(isFactor)
  );
}

/** The body, if it is the library this screen was written for; otherwise an error.
 *
 * **There is no branch that returns an empty library.** `{ sets: [] }` is a real answer (a
 * deployment the loader has not run against) and is returned as itself; anything that is not that
 * shape throws, so the two can never be rendered alike. */
export function parseFactorLibrary(body: unknown): FactorLibrary {
  if (!isRecord(body) || !Array.isArray(body.sets) || !body.sets.every(isSet)) {
    throw new Error(NOT_THE_SHAPE);
  }
  return { sets: body.sets };
}

export const factorKeys = {
  library: () => ["factor-library"] as const,
};

export function useFactorLibrary(enabled = true) {
  return useQuery({
    queryKey: factorKeys.library(),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<FactorLibrary> => {
      const response = await apiFetch("/api/v1/factor-sets");
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw refusalFrom(response, body, "The factor library could not be read.");
      }
      // A body that is not JSON is `null` here and fails `parseFactorLibrary` with its own sentence.
      const body: unknown = await response.json().catch(() => null);
      return parseFactorLibrary(body);
    },
  });
}
